import { GraphQLClient } from 'graphql-request';

import { type Mutation, type Query } from '@pkg/graphql/codegen/graphql';

import { makeUrl } from '../common/api/requests/makeUrl';
import { type GraphqlRequest } from '../common/structs/requests/request';
import {
    type GraphQLClientErrorResult,
    type GraphQLResponseError,
    type GraphQLResult,
} from '../common/structs/requests/result';

import { apiFactory } from './ApiFactory';

async function graphqlRequest<T>({ variables, query, settings, prefetch }: GraphqlRequest): Promise<GraphQLResult<T>> {
    const apiUrl = makeUrl({ settings, apiSettingsType: 'marketplace' });
    let token = await apiFactory().getToken(apiUrl);
    if (prefetch) {
        const prefetchToken = await prefetch();
        if (prefetchToken) {
            token = {
                ...token,
                access_token: prefetchToken,
                refresh_token: prefetchToken,
            };
        }
    }
    const headers = {
        credentials: settings?.credentials || 'include',
        referrerPolicy: 'no-referrer-when-downgrade',
    };
    // Append session token
    if (token) {
        const tokenString = token.access_token;
        if (tokenString) {
            headers['Authorization'] = `Bearer ${tokenString}`;
        }
    }

    const graphQLClient = new GraphQLClient(apiUrl, { headers, errorPolicy: 'all' });

    try {
        const requestRes = await graphQLClient.request<T>(query, variables);
        return {
            ...requestRes,
            data: requestRes,
        } as GraphQLResult<T>;
    } catch (e) {
        const error = e as GraphQLClientErrorResult<T>;
        return error.response as GraphQLResponseError<T>;
    }
}
export async function graphqlQuery(params: GraphqlRequest): Promise<GraphQLResult<Query>> {
    return await graphqlRequest<Query>(params);
}
export async function graphqlMutation(params: GraphqlRequest): Promise<GraphQLResult<Mutation>> {
    return await graphqlRequest<Mutation>(params);
}
